import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  Switch,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckIcon from '@mui/icons-material/Check';
import CasinoIcon from '@mui/icons-material/Casino';
import EditIcon from '@mui/icons-material/Edit';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import GroupIcon from '@mui/icons-material/Group';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import ShareIcon from '@mui/icons-material/Share';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fetchCompanions, fetchMyProfile } from '../api/companions';
import { fetchCompanionGames, fetchShelfGames } from '../api/library';
import {
  createNight,
  createNightShareInvitation,
  fetchNight,
  fetchNightPosition,
  updateNight,
  updateNightVotes,
} from '../api/nights';
import CompanionsEmptyState from '../components/companions/CompanionsEmptyState';
import InviteCompanionSheet from '../components/companions/InviteCompanionSheet';
import PresenceBadge from '../components/companions/PresenceBadge';
import BggRatingMugs from '../components/games/BggRatingMugs';
import GameFiltersBar, {
  defaultGameFilters,
} from '../components/games/GameFiltersBar';
import GameCover from '../components/tavern/GameCover';
import { useToast } from '../components/tavern/ToastProvider';
import ReportIssueButton from '../components/support/ReportIssueButton';
import { supportContextFromApiError } from '../utils/supportReports';
import { gameTitle } from '../components/games/gameFormat';
import {
  dateInputValueFromDate,
  dateInputValueFromDateTime,
  formatDisplayDate,
  formatDisplayTime,
  formatDisplayWeekday,
  timeInputValueFromDateTime,
} from '../i18n/formatters';
import {
  NIGHT_TITLE_MAX_LENGTH,
  clipNightTitleInput,
  normalizeNightTitle,
} from '../utils/nightTitle';
import { FUTURE_NIGHTS_PAGE_SIZE } from '../utils/nightPagination';
import {
  clearNightDraft,
  isBrowserReloadNavigation,
  readNightDraft,
  saveNightDraft,
  shouldResetNewNightDraftState,
  shouldReadNightDraft,
} from '../utils/nightDraftStorage';
import { filterCompanionsForInvitation } from '../utils/nightCompanionSearch';

const DEFAULT_SESSION_TIME = '19:00';
const DEFAULT_SESSION_HOUR = 19;
const NIGHT_VOTE_DEFAULT_SORT = 'mostVoted';
const OWNER_COLORS = ['#d99a2b', '#0f766e', '#b4471f', '#6f5aa8', '#2f8f46', '#be4f87'];
const INITIAL_BROWSER_PATHNAME =
  typeof window !== 'undefined' ? window.location.pathname.replace(/\/+$/, '') || '/' : '';

const hashString = (value = '') =>
  String(value)
    .split('')
    .reduce((total, char) => total + char.charCodeAt(0), 0);

const ownerColor = (owner, index = 0) =>
  OWNER_COLORS[
    (Number(owner?.id) || hashString(owner?.displayName) || index) %
      OWNER_COLORS.length
  ];

const asArray = (value) => (Array.isArray(value) ? value : []);

const hasValidGameId = (game) => {
  const id = Number(game?.id);

  return Number.isInteger(id) && id > 0;
};

const validGames = (games = []) => asArray(games).filter(hasValidGameId);

const gameTimestamp = (game) => new Date(game?.createdAt || game?.updatedAt || 0).getTime() || 0;

const sortGameOptions = (items, sort, t, language) => {
  const sorted = [...items];

  if (sort === 'name') {
    return sorted.sort((left, right) =>
      gameTitle(left, t, language).localeCompare(gameTitle(right, t, language), language, {
        sensitivity: 'base',
      })
    );
  }

  if (sort === 'timesPlayed') {
    return sorted.sort(
      (left, right) =>
        Number(right.timesPlayed || 0) - Number(left.timesPlayed || 0) ||
        gameTitle(left, t, language).localeCompare(gameTitle(right, t, language), language, {
          sensitivity: 'base',
        })
    );
  }

  return sorted.sort((left, right) => gameTimestamp(right) - gameTimestamp(left));
};

const sortGamesByName = (items, t, language) =>
  [...items].sort((left, right) =>
    gameTitle(left, t, language).localeCompare(gameTitle(right, t, language), language, {
      sensitivity: 'base',
    })
  );

const gameGroupKey = (game) =>
  game?.bggId ? `bgg:${game.bggId}` : `title:${gameTitle(game).trim().toLowerCase()}`;

const compactOwners = (owners) =>
  Array.from(
    new Map(
      owners
        .filter((owner) => owner?.id || owner?.displayName)
        .map((owner) => [owner.id || owner.displayName, owner])
    ).values()
  );

const compactPeople = (people) =>
  Array.from(
    new Map(
      people
        .filter((person) => person?.id || person?.displayName)
        .map((person) => [person.id || person.displayName, person])
    ).values()
  );

const nightShareUrl = (token, origin = window.location.origin) =>
  `${origin}/invite/gathering/${encodeURIComponent(String(token || ''))}`;

const nightShareText = ({ inviterName, t, title, url }) =>
  t('nights.share.text', {
    inviterName: inviterName || t('profile.defaults.displayName'),
    title,
    url,
  });

const ownerFromGame = (game, fallbackOwner) => ({
  id: game?.ownerId || game?.userId || fallbackOwner?.id,
  displayName: game?.ownerName || fallbackOwner?.displayName,
  avatarUrl: game?.ownerAvatarUrl || fallbackOwner?.avatarUrl,
});

const gameOwnerIds = (game) =>
  Array.from(
    new Set(
      [
        game?.ownerId,
        game?.userId,
        ...(game?.owners || []).map((owner) => owner?.id),
      ]
        .map(Number)
        .filter((id) => Number.isInteger(id) && id > 0)
    )
  );

export const eligibleNightGameOptions = ({
  games = [],
  invited = [],
  requiresResponse = true,
  selfProfile = null,
  eligibleOwnerIds = null,
}) => {
  const explicitEligibleOwners = Array.isArray(eligibleOwnerIds)
    ? eligibleOwnerIds.map(Number).filter((id) => Number.isInteger(id) && id > 0)
    : null;
  const fallbackEligibleOwners = [
    Number(selfProfile?.id),
    ...(requiresResponse ? [] : invited.map(Number)),
  ].filter((id) => Number.isInteger(id) && id > 0);
  const allowedOwners = new Set(explicitEligibleOwners || fallbackEligibleOwners);

  if (allowedOwners.size === 0) return [];

  return games.filter((game) => gameOwnerIds(game).some((ownerId) => allowedOwners.has(ownerId)));
};

const mergeGameOptions = (shelfGames, companionGames, selfProfile) => {
  const groups = new Map();
  const selfOwner = selfProfile
    ? {
        id: selfProfile.id,
        displayName: selfProfile.displayName,
        avatarUrl: selfProfile.avatarUrl,
      }
    : null;

  [
    ...validGames(shelfGames).map((game) => ({ game, fallbackOwner: selfOwner })),
    ...validGames(companionGames).map((game) => ({ game })),
  ].forEach(({ game, fallbackOwner }) => {
      const key = gameGroupKey(game);
      const current = groups.get(key);
      const owner = ownerFromGame(game, fallbackOwner);
      const entry = current || {
        ...game,
        gameIds: [],
        owners: [],
      };

      entry.gameIds = Array.from(new Set([...entry.gameIds, game.id]));
      entry.owners = compactOwners([...entry.owners, owner]);

      if (!current || (selfOwner && owner.id === selfOwner.id)) {
        groups.set(key, { ...entry, ...game, gameIds: entry.gameIds, owners: entry.owners });
      } else {
        groups.set(key, entry);
      }
  });

  return Array.from(groups.values());
};

const getDefaultSessionDate = () => {
  const date = new Date();

  if (date.getHours() >= DEFAULT_SESSION_HOUR) {
    date.setDate(date.getDate() + 1);
  }

  date.setHours(0, 0, 0, 0);

  return dateInputValueFromDate(date);
};

const timeInputValueFromDate = (date) => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
};

const getTodayInputValue = () => dateInputValueFromDate(new Date());

const getCurrentTimeInputValue = () => {
  const date = new Date();
  date.setSeconds(0, 0);

  return timeInputValueFromDate(date);
};

const isBeforeToday = (dateValue) => Boolean(dateValue && dateValue < getTodayInputValue());

const isToday = (dateValue) => dateValue === getTodayInputValue();

const isScheduledInPast = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) return false;

  const scheduledAt = new Date(`${dateValue}T${timeValue}:00`);
  const currentMinute = new Date();
  currentMinute.setSeconds(0, 0);

  return !Number.isNaN(scheduledAt.getTime()) && scheduledAt < currentMinute;
};

const optional = (promise, fallback) =>
  promise.catch((error) => {
    const status = error?.response?.status;

    if (status === 401 || status === 403) {
      throw error;
    }

    return fallback;
  });

const stepFromQuery = (value) => {
  const stepMap = {
    gathering: 0,
    hour: 1,
    place: 2,
    companions: 3,
    games: 4,
    review: 5,
  };

  return stepMap[value] ?? 0;
};

const companionIdFromQuery = (value) => {
  const id = Number(value);

  return Number.isInteger(id) && id > 0 ? id : null;
};

const newNightInitialStep = (requestedStep, requestedCompanionId, isEditing = false) => {
  if (!isEditing && requestedCompanionId && requestedStep === 'companions') {
    return 0;
  }

  return stepFromQuery(requestedStep);
};

const shouldResumeDraftStep = ({ isEditing, requestedCompanionId, requestedStep }) =>
  !requestedStep && (isEditing || !requestedCompanionId);

const nightIdFromResponse = (response, fallbackId = null) => {
  const candidates = [
    response?.id,
    response?.night?.id,
    response?.item?.id,
    response?.data?.id,
    fallbackId,
  ];

  const id = candidates
    .map((candidate) => Number(candidate))
    .find((candidate) => Number.isInteger(candidate) && candidate > 0);

  return id || null;
};

const validVoteResults = (results = []) =>
  (Array.isArray(results) ? results : []).filter((result) => {
    const gameId = Number(result?.gameId);

    return Number.isInteger(gameId) && gameId > 0;
  });

const NewNightPage = () => {
  const navigate = useNavigate();
  const { nightId } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const isEditing = Boolean(nightId);
  const [step, setStep] = useState(() =>
    newNightInitialStep(
      searchParams.get('step'),
      companionIdFromQuery(searchParams.get('companionId')),
      isEditing
    )
  );
  const steps = [
    { label: t('newNight.steps.gathering.label'), title: t('newNight.steps.gathering.title'), icon: EditIcon },
    { label: t('newNight.steps.hour.label'), title: t('newNight.steps.hour.title'), icon: CalendarMonthIcon },
    { label: t('newNight.steps.place.label'), title: t('newNight.steps.place.title'), icon: LocationOnIcon },
    { label: t('newNight.steps.companions.label'), title: t('newNight.steps.companions.title'), icon: GroupIcon },
    { label: t('newNight.steps.games.label'), title: t('newNight.steps.games.title'), icon: CasinoIcon },
    { label: t('newNight.steps.review.label'), title: t('newNight.steps.review.title'), icon: FactCheckIcon },
  ];
  const [title, setTitle] = useState('');
  const defaultPlaceName = t('newNight.placeOptions.myPlace');
  const [date, setDate] = useState(getDefaultSessionDate);
  const [time, setTime] = useState(DEFAULT_SESSION_TIME);
  const [placePreset, setPlacePreset] = useState('myPlace');
  const [place, setPlace] = useState(defaultPlaceName);
  const [invited, setInvited] = useState([]);
  const [companionSearchQuery, setCompanionSearchQuery] = useState('');
  const [chosenGames, setChosenGames] = useState([]);
  const [gameSearchQuery, setGameSearchQuery] = useState('');
  const [gameFilters, setGameFilters] = useState(defaultGameFilters);
  const [gameSort, setGameSort] = useState(NIGHT_VOTE_DEFAULT_SORT);
  const [requiresResponse, setRequiresResponse] = useState(true);
  const [voteOnly, setVoteOnly] = useState(false);
  const [voteResults, setVoteResults] = useState([]);
  const [gameFiltersTouched, setGameFiltersTouched] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [externalInviteSharing, setExternalInviteSharing] = useState(false);
  const [companions, setCompanions] = useState([]);
  const [allGameOptions, setAllGameOptions] = useState([]);
  const [selfProfile, setSelfProfile] = useState(null);
  const [confirmedVoteOwnerIds, setConfirmedVoteOwnerIds] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [loadErrorContext, setLoadErrorContext] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveErrorContext, setSaveErrorContext] = useState(null);
  const [draftReady, setDraftReady] = useState(false);
  const canReadDraftRef = useRef(
    shouldReadNightDraft({
      isEditing,
      isReloadNavigation: isBrowserReloadNavigation(),
      isInitialNewNightRoute: INITIAL_BROWSER_PATHNAME === '/nights/new',
    })
  );
  const savingRef = useRef(false);
  const completedNewNightFlowRef = useRef(false);
  const voteScrollAnchorRef = useRef(null);
  const language = i18n.resolvedLanguage || i18n.language;
  const requestedStep = searchParams.get('step');
  const requestedCompanionId = companionIdFromQuery(searchParams.get('companionId'));
  const nightVoteSortOptions = useMemo(
    () => [
      { value: 'mostVoted', labelKey: 'newNight.games.sortMostVoted' },
      { value: 'name', labelKey: 'shelf.sort.name' },
      { value: 'recent', labelKey: 'shelf.sort.recent' },
      { value: 'timesPlayed', labelKey: 'shelf.sort.timesPlayed' },
    ],
    []
  );
  const placeOptions = useMemo(
    () => [
      {
        id: 'myPlace',
        label: t('newNight.placeOptions.myPlace'),
        value: t('newNight.placeOptions.myPlace'),
        description: t('newNight.placeOptions.myPlaceDescription'),
      },
      {
        id: 'companionPlace',
        label: t('newNight.placeOptions.companionPlace'),
        value: t('newNight.placeOptions.companionPlace'),
        description: t('newNight.placeOptions.companionPlaceDescription'),
      },
      {
        id: 'customPlace',
        label: t('newNight.placeOptions.customPlace'),
        value: t('newNight.placeOptions.customPlaceDefault'),
        description: t('newNight.placeOptions.customPlaceDescription'),
      },
    ],
    [t]
  );

  const resetNewNightDraftState = useCallback(() => {
    completedNewNightFlowRef.current = false;
    canReadDraftRef.current = false;
    clearNightDraft(null);
    setDraftReady(false);
    setVoteOnly(false);
    setStep(newNightInitialStep(requestedStep, requestedCompanionId, false));
    setTitle('');
    setDate(getDefaultSessionDate());
    setTime(DEFAULT_SESSION_TIME);
    setPlacePreset('myPlace');
    setPlace(defaultPlaceName);
    setInvited([]);
    setCompanionSearchQuery('');
    setChosenGames([]);
    setGameSearchQuery('');
    setGameFilters(defaultGameFilters);
    setGameSort(NIGHT_VOTE_DEFAULT_SORT);
    setRequiresResponse(true);
    setVoteResults([]);
    setGameFiltersTouched(false);
    setSaveError('');
    setSaveErrorContext(null);
  }, [defaultPlaceName, requestedCompanionId, requestedStep]);

  useEffect(() => {
    if (placePreset === 'myPlace') {
      setPlace(defaultPlaceName);
    }
  }, [defaultPlaceName, placePreset]);

  useEffect(() => {
    setStep(newNightInitialStep(requestedStep, requestedCompanionId, isEditing));
  }, [isEditing, requestedCompanionId, requestedStep]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (
        shouldResetNewNightDraftState({
          isEditing,
          isPersistedPageShow: event.persisted === true,
          hasCompletedNewNightFlow: completedNewNightFlowRef.current,
        })
      ) {
        resetNewNightDraftState();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [isEditing, resetNewNightDraftState]);

  useEffect(() => {
    let mounted = true;

    setLoadingOptions(true);
    setLoadError('');
    setLoadErrorContext(null);
    setDraftReady(false);

    if (!canReadDraftRef.current) {
      clearNightDraft(nightId);
    }

    if (!isEditing) {
      setVoteOnly(false);
      setTitle('');
      setDate(getDefaultSessionDate());
      setTime(DEFAULT_SESSION_TIME);
      setPlacePreset('myPlace');
      setPlace(defaultPlaceName);
      setInvited([]);
      setCompanionSearchQuery('');
      setChosenGames([]);
      setAllGameOptions([]);
      setSelfProfile(null);
      setConfirmedVoteOwnerIds(null);
      setVoteResults([]);
      setGameSearchQuery('');
      setGameFilters(defaultGameFilters);
      setGameSort(NIGHT_VOTE_DEFAULT_SORT);
      setRequiresResponse(true);
      setGameFiltersTouched(false);
      setStep(newNightInitialStep(requestedStep, requestedCompanionId, isEditing));
    }

    Promise.all([
      optional(fetchCompanions({ limit: 50, page: 1 }), { items: [] }),
      optional(fetchMyProfile(), null),
      optional(fetchShelfGames({ limit: 48, page: 1 }), { items: [] }),
      optional(fetchCompanionGames({ limit: 48 }), { items: [] }),
      isEditing ? fetchNight(nightId) : Promise.resolve(null),
    ])
      .then(([companionsData, profileData, shelfData, companionGamesData, nightData]) => {
        if (!mounted) return;

        const nextCompanions = companionsData.items || [];
        const availableNightGames = nightData?.availableGames || [];
        const nextConfirmedVoteOwnerIds = nightData
          ? [nightData.host, ...(nightData.companions || [])]
              .map((person) => Number(person?.id))
              .filter((id) => Number.isInteger(id) && id > 0)
          : null;
        const gameOptions = mergeGameOptions(
          [...(shelfData.items || []), ...availableNightGames],
          companionGamesData.items || [],
          profileData
        );

        setSelfProfile(profileData);
        setCompanions(nextCompanions);
        setConfirmedVoteOwnerIds(nextConfirmedVoteOwnerIds);
        setAllGameOptions(gameOptions);

        let voteOnlyMode = false;

        if (nightData) {
          const canEditNight = nightData.permissions?.canEdit ?? nightData.hostId === nightData.viewerId;
          const canVoteNight = nightData.permissions?.canVote === true;
          voteOnlyMode = !canEditNight && canVoteNight;

          if (!canEditNight && !canVoteNight) {
            toast.error(t('newNight.feedback.organizerOnly'));
            navigate('/nights', {
              replace: true,
              state: { toast: t('newNight.feedback.organizerOnly') },
            });
            return;
          }

          setVoteOnly(voteOnlyMode);
          if (voteOnlyMode) {
            setStep(4);
          }

          const nextPlace = nightData.locationName || defaultPlaceName;
          const matchingPlace = placeOptions.find((option) => option.value === nextPlace);

          setTitle(normalizeNightTitle(nightData.title));
          setDate(dateInputValueFromDateTime(nightData.scheduledAt) || getDefaultSessionDate());
          setTime(timeInputValueFromDateTime(nightData.scheduledAt) || DEFAULT_SESSION_TIME);
          setPlace(nextPlace);
          setPlacePreset(matchingPlace?.id || 'customPlace');
          setInvited((nightData.companions || []).map((companion) => companion.id));
          setVoteResults(validVoteResults(nightData.voting?.results));
          setRequiresResponse(nightData.requiresResponse !== false);
          setChosenGames(
            (nightData.voting?.viewerGameIds || []).length > 0
              ? (nightData.voting.viewerGameIds || []).map(Number)
              : voteOnlyMode
                ? []
                : (nightData.games || []).map((game) => game.id)
          );
        } else {
          setInvited([]);
          setChosenGames([]);
          setVoteResults([]);
          setConfirmedVoteOwnerIds(null);
        }

        const draft = canReadDraftRef.current && !voteOnlyMode ? readNightDraft(nightId) : null;
        if (draft) {
          setTitle(draft.title);
          setDate(draft.date || getDefaultSessionDate());
          setTime(draft.time || DEFAULT_SESSION_TIME);
          setPlacePreset(draft.placePreset || 'myPlace');
          setPlace(draft.place || defaultPlaceName);
          setInvited(
            draft.invited.filter((id) =>
              nextCompanions.some((companion) => Number(companion.id) === Number(id))
            )
          );
          setChosenGames(
            draft.chosenGames.filter((id) =>
              eligibleNightGameOptions({
                games: gameOptions,
                invited: draft.invited,
                requiresResponse: draft.requiresResponse !== false,
                selfProfile: profileData,
                eligibleOwnerIds: nextConfirmedVoteOwnerIds,
              }).some((game) =>
                (game.gameIds || [game.id]).map(Number).includes(Number(id))
              )
            )
          );
          setGameSearchQuery(draft.gameSearchQuery);
          setGameFilters(draft.gameFilters);
          setGameSort(draft.gameSort || NIGHT_VOTE_DEFAULT_SORT);
          setRequiresResponse(draft.requiresResponse !== false);
          setGameFiltersTouched(
            Boolean(draft.gameSearchQuery || Object.values(draft.gameFilters).some(Boolean))
          );

          if (shouldResumeDraftStep({ isEditing, requestedCompanionId, requestedStep })) {
            setStep(Math.max(0, Math.min(draft.step, steps.length - 1)));
          }
        }

        if (!isEditing && requestedCompanionId) {
          const companionExists = nextCompanions.some(
            (companion) => Number(companion.id) === requestedCompanionId
          );

          if (companionExists) {
            setInvited((current) =>
              current.some((id) => Number(id) === requestedCompanionId)
                ? current
                : [...current, requestedCompanionId]
            );
          }
        }

        setDraftReady(true);
      })
      .catch((error) => {
        if (mounted) {
          setLoadError(t('newNight.feedback.loadError'));
          setLoadErrorContext(
            supportContextFromApiError(error, {
              pageName: isEditing ? 'Edit Game Night' : 'New Game Night',
            })
          );
        }
      })
      .finally(() => {
        if (mounted) setLoadingOptions(false);
      });

    return () => {
      mounted = false;
    };
  }, [
    defaultPlaceName,
    isEditing,
    navigate,
    nightId,
    placeOptions,
    requestedCompanionId,
    requestedStep,
    steps.length,
    t,
    toast,
  ]);

  useEffect(() => {
    if (voteOnly || !draftReady || loadingOptions || loadError) return;

    saveNightDraft(nightId, {
      step,
      title,
      date,
      time,
      place,
      placePreset,
      invited,
      chosenGames,
      gameSearchQuery,
      gameFilters,
      gameSort,
      requiresResponse,
    });
  }, [
    chosenGames,
    date,
    draftReady,
    gameFilters,
    gameSearchQuery,
    gameSort,
    invited,
    loadError,
    loadingOptions,
    nightId,
    place,
    placePreset,
    requiresResponse,
    step,
    time,
    title,
    voteOnly,
  ]);

  const progress = ((step + 1) / steps.length) * 100;
  const isLast = step === steps.length - 1;
  const actionIsLast = voteOnly || isLast;
  const minDate = getTodayInputValue();
  const minTime = isToday(date) ? getCurrentTimeInputValue() : undefined;
  const scheduleIsPast = isBeforeToday(date) || isScheduledInPast(date, time);
  const selectedCompanionNames = useMemo(
    () =>
      companions
        .filter((companion) => invited.includes(companion.id))
        .map((companion) => companion.displayName),
    [companions, invited]
  );
  const visibleCompanions = useMemo(
    () => filterCompanionsForInvitation(companions, companionSearchQuery),
    [companions, companionSearchQuery]
  );
  const recommendedMinPlayers = invited.length + 1;
  const generatedTitle = useMemo(() => {
    const weekday = formatDisplayWeekday(date, language).toLowerCase();
    const day = weekday || t('newNight.generatedTitle.defaultDay');
    const companionList =
      selectedCompanionNames.length > 0
        ? new Intl.ListFormat(language, { style: 'long', type: 'conjunction' }).format(
            selectedCompanionNames
          )
        : '';

    return companionList
      ? t('newNight.generatedTitle.withCompanions', {
          day,
          companions: companionList,
        })
      : t('newNight.generatedTitle.withoutCompanions', { day });
  }, [date, language, selectedCompanionNames, t]);
  const nightTitle = normalizeNightTitle(title || generatedTitle);
  const titleCounterColor =
    title.length >= NIGHT_TITLE_MAX_LENGTH ? 'secondary.dark' : 'text.secondary';
  const games = useMemo(
    () =>
      eligibleNightGameOptions({
        games: allGameOptions,
        invited,
        requiresResponse,
        selfProfile,
        eligibleOwnerIds: confirmedVoteOwnerIds,
      }),
    [allGameOptions, confirmedVoteOwnerIds, invited, requiresResponse, selfProfile]
  );
  const serverVoteVotersByGameId = useMemo(() => {
    const nextVotes = new Map();

    validVoteResults(voteResults).forEach((result) => {
      nextVotes.set(Number(result.gameId), Array.isArray(result.voters) ? result.voters : []);
    });

    return nextVotes;
  }, [voteResults]);
  const optimisticVotersForGame = useCallback((game) => {
    const relatedIds = game.gameIds || [game.id];
    const selected = relatedIds.some((id) => chosenGames.includes(id));
    const withoutViewer = compactPeople(
      relatedIds.flatMap((id) => serverVoteVotersByGameId.get(Number(id)) || [])
    ).filter((voter) => Number(voter.id) !== Number(selfProfile?.id));

    return selected && selfProfile
      ? compactPeople([selfProfile, ...withoutViewer])
      : withoutViewer;
  }, [chosenGames, selfProfile, serverVoteVotersByGameId]);
  const voteCountForGame = useCallback(
    (game) => optimisticVotersForGame(game).length,
    [optimisticVotersForGame]
  );
  const visibleGames = useMemo(() => {
    const query = gameSearchQuery.trim().toLowerCase();
    const minPlayers = Number(gameFilters.minPlayers) || null;
    const maxPlayers = Number(gameFilters.maxPlayers) || null;
    const maxTime = Number(gameFilters.maxTime) || null;

    const filtered = games.filter((game) => {
      const titleMatches = !query || gameTitle(game, t, language).toLowerCase().includes(query);
      const minPlayersMatch = !minPlayers || (game.maxPlayers || 0) >= minPlayers;
      const maxPlayersMatch = !maxPlayers || (game.minPlayers || 0) <= maxPlayers;
      const maxTimeMatch = !maxTime || (game.playingTime || game.maxPlayTime || 0) <= maxTime;

      return titleMatches && minPlayersMatch && maxPlayersMatch && maxTimeMatch;
    });

    if (gameSort === NIGHT_VOTE_DEFAULT_SORT) {
      return sortGamesByName(filtered, t, language);
    }

    return sortGameOptions(filtered, gameSort, t, language);
  }, [gameFilters, gameSearchQuery, gameSort, games, language, t]);
  const votedGames = useMemo(
    () =>
      games
        .map((game) => ({
          game,
          voters: optimisticVotersForGame(game),
          voteCount: voteCountForGame(game),
        }))
        .filter((item) => item.voteCount > 0)
        .sort(
          (left, right) =>
            right.voteCount - left.voteCount ||
            gameTitle(left.game, t, language).localeCompare(
              gameTitle(right.game, t, language),
              language,
              { sensitivity: 'base' }
            )
        ),
    [games, language, optimisticVotersForGame, t, voteCountForGame]
  );
  const chosenGameOptions = useMemo(
    () =>
      games.filter((game) => {
        const relatedIds = game.gameIds || [game.id];

        return relatedIds.some((id) => chosenGames.includes(id));
      }),
    [chosenGames, games]
  );
  useEffect(() => {
    const eligibleGameIds = new Set(
      games.flatMap((game) => game.gameIds || [game.id]).map(Number)
    );

    setChosenGames((current) => {
      const next = current.filter((gameId) => eligibleGameIds.has(Number(gameId)));

      return next.length === current.length ? current : next;
    });
  }, [games]);
  const visibleGameGroups = useMemo(() => {
    const groups = new Map();

    visibleGames.forEach((game) => {
      const owners = game.owners?.length ? game.owners : [ownerFromGame(game)];
      const primaryOwner = owners[0] || {};
      const key = primaryOwner.id || primaryOwner.displayName || 'unknown';
      const current = groups.get(key) || {
        owner: primaryOwner,
        games: [],
      };

      current.games.push(game);
      groups.set(key, current);
    });

    return Array.from(groups.values());
  }, [visibleGames]);
  const reviewScheduledAt = `${date}T${time || DEFAULT_SESSION_TIME}:00`;
  const reviewDate = formatDisplayDate(reviewScheduledAt, language);
  const reviewWeekday = formatDisplayWeekday(reviewScheduledAt, language);
  const reviewTime = formatDisplayTime(time, language);
  const reviewVotingClosesAt = useMemo(() => {
    const scheduledAt = new Date(reviewScheduledAt);
    if (Number.isNaN(scheduledAt.getTime())) return null;

    scheduledAt.setHours(scheduledAt.getHours() - 24);
    return scheduledAt;
  }, [reviewScheduledAt]);
  const reviewVotingCloseDate = reviewVotingClosesAt
    ? formatDisplayDate(reviewVotingClosesAt.toISOString(), language)
    : '';
  const reviewVotingCloseTime = reviewVotingClosesAt
    ? formatDisplayTime(timeInputValueFromDate(reviewVotingClosesAt), language)
    : '';
  const isStepComplete = (stepIndex) => {
    if (stepIndex === 0) return true;
    if (stepIndex === 1) return Boolean(date && time && !scheduleIsPast);
    if (stepIndex === 2) return Boolean(place.trim());
    return true;
  };
  const canContinue = isStepComplete(step);
  const canVisitStep = (stepIndex) =>
    voteOnly
      ? stepIndex === 4
      : stepIndex <= step ||
        Array.from({ length: stepIndex }).every((_, index) => isStepComplete(index));
  const goToStep = (stepIndex) => {
    if (canVisitStep(stepIndex)) setStep(stepIndex);
  };

  useEffect(() => {
    if (step !== 4 || gameFiltersTouched) return;

    setGameFilters((current) => {
      if (Number(current.minPlayers) === recommendedMinPlayers) return current;

      return {
        ...current,
        minPlayers: recommendedMinPlayers,
      };
    });
  }, [gameFiltersTouched, recommendedMinPlayers, step]);

  const toggleFriend = (id) =>
    setInvited((current) =>
      current.includes(id)
        ? current.filter((friendId) => friendId !== id)
        : [...current, id]
    );

  const handleGameFiltersChange = (nextFilters) => {
    setGameFiltersTouched(true);
    setGameFilters(nextFilters);
  };

  const handleDateChange = (value) => {
    const nextDate = isBeforeToday(value) ? getTodayInputValue() : value;
    setDate(nextDate);

    if (isScheduledInPast(nextDate, time)) {
      setTime(getCurrentTimeInputValue());
    }
  };

  const handleTimeChange = (value) => {
    const nextTime =
      isToday(date) && isScheduledInPast(date, value)
        ? getCurrentTimeInputValue()
        : value;

    setTime(nextTime);
  };

  const rememberVoteScrollAnchor = (element) => {
    if (step !== 4 || !element) return;

    voteScrollAnchorRef.current = {
      element,
      top: element.getBoundingClientRect().top,
    };
  };

  useLayoutEffect(() => {
    const anchor = voteScrollAnchorRef.current;
    if (!anchor) return;

    voteScrollAnchorRef.current = null;

    if (!anchor.element?.isConnected) return;

    const nextTop = anchor.element.getBoundingClientRect().top;
    const delta = nextTop - anchor.top;

    if (Math.abs(delta) > 1) {
      window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
    }
  }, [chosenGames]);

  const toggleGame = (game) => {
    const relatedIds = game.gameIds || [game.id];
    const preferredId = relatedIds.includes(game.id) ? game.id : relatedIds[0];

    setChosenGames((current) => {
      const selected = relatedIds.some((id) => current.includes(id));

      return selected
        ? current.filter((gameId) => !relatedIds.includes(gameId))
        : [...current, preferredId];
    });
  };

  const handleSealNight = async () => {
    if (savingRef.current) return;

    savingRef.current = true;
    setSaving(true);
    setSaveError('');
    setSaveErrorContext(null);

    try {
      if (voteOnly) {
        await updateNightVotes(nightId, chosenGames);
        setDraftReady(false);
        clearNightDraft(nightId);
        navigate(`/nights?highlightNight=${nightId}`, {
          state: { toast: t('nights.votes.saved') },
        });
        return;
      }

      const payload = {
        title: nightTitle,
        date,
        time,
        locationName: place.trim(),
        locationAddress: null,
        companionIds: invited,
        gameIds: chosenGames,
        requiresResponse,
      };

      const savedNight = isEditing
        ? await updateNight(nightId, payload)
        : await createNight(payload);
      const savedNightId = nightIdFromResponse(savedNight, nightId);
      let targetPage = 1;

      setDraftReady(false);
      clearNightDraft(nightId);
      if (!isEditing) {
        completedNewNightFlowRef.current = true;
      }

      if (savedNightId) {
        try {
          const position = await fetchNightPosition(savedNightId, {
            scope: 'future',
            limit: FUTURE_NIGHTS_PAGE_SIZE,
          });

          targetPage = position?.page || 1;
        } catch {
          targetPage = 1;
        }
      }

      navigate(`/nights?page=${targetPage}${savedNightId ? `&highlightNight=${savedNightId}` : ''}`, {
        state: {
          toast: t(
            isEditing ? 'newNight.feedback.updated' : 'newNight.feedback.created'
          ),
        },
      });
    } catch (error) {
      setSaveError(t('newNight.feedback.saveError'));
      setSaveErrorContext(
        supportContextFromApiError(error, {
          pageName: isEditing ? 'Edit Game Night' : 'New Game Night',
        })
      );
      toast.error(t('newNight.feedback.saveError'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleExternalInvite = async () => {
    if (!isEditing || !nightId) {
      setInviteOpen(true);
      return;
    }

    if (externalInviteSharing) return;

    setExternalInviteSharing(true);

    try {
      const invitation = await createNightShareInvitation(nightId);
      const url = nightShareUrl(invitation.token);
      const shareTitle = t('nights.share.title', { title: nightTitle });
      const text = nightShareText({
        inviterName: selfProfile?.displayName,
        t,
        title: nightTitle,
        url,
      });

      if (navigator.share) {
        try {
          await navigator.share({ title: shareTitle, text, url });
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }

      await navigator.clipboard?.writeText(text);
      toast.success(t('nights.feedback.shareCopied'));
    } catch {
      toast.error(t('nights.feedback.shareError'));
    } finally {
      setExternalInviteSharing(false);
    }
  };

  const handleBackToNights = () => {
    setDraftReady(false);
    clearNightDraft(nightId);
    if (!isEditing) {
      completedNewNightFlowRef.current = true;
    }
    navigate('/nights');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        overflowX: 'hidden',
        pb: { xs: 3, md: 5 },
      }}
    >
      <Stack spacing={3} sx={{ minWidth: 0 }}>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBackToNights}>
            {t('newNight.actions.backToNights')}
          </Button>
        </Box>

        <Box>
            <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
              {t('newNight.progress', {
                current: step + 1,
                total: steps.length,
              })}
            </Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: 30, md: 40 } }}>
            {t('newNight.title')}
          </Typography>
        </Box>

        <Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 999,
              bgcolor: 'rgba(15, 76, 69, 0.12)',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'secondary.main',
              },
            }}
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${steps.length}, 1fr)`,
              gap: 1,
              mt: 2,
            }}
          >
            {steps.map(({ icon: Icon, label }, index) => {
              const done = index < step;
              const current = index === step;

              return (
                <ButtonBase
                  key={label}
                  component="button"
                  type="button"
                  disabled={!canVisitStep(index)}
                  onClick={() => goToStep(index)}
                  aria-current={current ? 'step' : undefined}
                  aria-label={`${label}: ${steps[index].title}`}
                  sx={{
                    minWidth: 0,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.75,
                    py: 0.75,
                    opacity: canVisitStep(index) ? 1 : 0.52,
                    cursor: canVisitStep(index) ? 'pointer' : 'not-allowed',
                    '&:focus-visible': {
                      outline: '3px solid',
                      outlineColor: 'secondary.light',
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      border: '1px solid',
                      borderColor: current ? 'primary.main' : 'divider',
                      bgcolor: done
                        ? 'secondary.main'
                        : current
                          ? 'primary.main'
                          : 'background.paper',
                      color: done || current ? 'primary.contrastText' : 'text.secondary',
                    }}
                  >
                    {done ? <CheckIcon fontSize="small" /> : <Icon fontSize="small" />}
                  </Box>
                  <Typography
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      fontSize: '0.6rem',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                    }}
                  >
                    {label}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Box>
        </Box>

        <Card variant="outlined" sx={{ borderRadius: 3, minWidth: 0 }}>
          <CardContent
            sx={{
              p: { xs: 3, md: 4 },
              pb: {
                xs: 'calc(11rem + env(safe-area-inset-bottom, 0px))',
                md: 4,
              },
              minHeight: { md: 420 },
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            {loadError ? (
              <Alert
                severity="error"
                sx={{ mb: 3 }}
                action={
                  <ReportIssueButton
                    context={loadErrorContext}
                    sx={{ whiteSpace: 'nowrap' }}
                  />
                }
              >
                {loadError}
              </Alert>
            ) : null}
            {saveError ? (
              <Alert
                severity="error"
                sx={{ mb: 3 }}
                action={
                  <ReportIssueButton
                    context={saveErrorContext}
                    sx={{ whiteSpace: 'nowrap' }}
                  />
                }
              >
                {saveError}
              </Alert>
            ) : null}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {t('newNight.stepLabel', {
                step: step + 1,
              })}
            </Typography>
            <Typography variant="h2" sx={{ mb: 4 }}>
              {steps[step].title}
            </Typography>

            {step === 0 && (
              <Stack spacing={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('newNight.fields.titleOptional')}
                </Typography>
                <TextField
                  value={title}
                  onChange={(event) => setTitle(clipNightTitleInput(event.target.value))}
                  placeholder={t('newNight.placeholders.title')}
                  fullWidth
                  helperText={t('newNight.helpers.titleCounter', {
                    count: title.length,
                    max: NIGHT_TITLE_MAX_LENGTH,
                  })}
                  inputProps={{
                    maxLength: NIGHT_TITLE_MAX_LENGTH,
                    style: { fontFamily: 'Cinzel, Georgia, serif', fontSize: 22 },
                  }}
                  FormHelperTextProps={{
                    sx: {
                      textAlign: 'right',
                      color: titleCounterColor,
                      fontWeight: title.length >= NIGHT_TITLE_MAX_LENGTH ? 800 : 500,
                    },
                  }}
                />
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  {t('newNight.tips.title')}
                </Typography>
              </Stack>
            )}

            {step === 1 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <TextField
                  label={t('newNight.fields.date')}
                  type="date"
                  value={date}
                  onChange={(event) => handleDateChange(event.target.value)}
                  inputProps={{ min: minDate }}
                  error={scheduleIsPast}
                  helperText={
                    scheduleIsPast
                      ? t('newNight.feedback.pastSchedule')
                      : ' '
                  }
                />
                <TextField
                  label={t('newNight.fields.time')}
                  type="time"
                  value={time}
                  onChange={(event) => handleTimeChange(event.target.value)}
                  inputProps={minTime ? { min: minTime } : undefined}
                  error={scheduleIsPast}
                  helperText=" "
                />
              </Box>
            )}

            {step === 2 && (
              <Stack spacing={2.5}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('newNight.fields.placePrompt')}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: 1.25,
                  }}
                >
                  {placeOptions.map((option) => (
                    <ButtonBase
                      key={option.id}
                      type="button"
                      onClick={() => {
                        setPlacePreset(option.id);
                        setPlace(option.value);
                      }}
                      sx={{
                        minHeight: 88,
                        border: '1px solid',
                        borderColor: placePreset === option.id ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        bgcolor: placePreset === option.id ? 'rgba(15, 76, 69, 0.08)' : 'background.paper',
                        p: 1.5,
                        textAlign: 'left',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Typography fontWeight={800}>{option.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Stack>
                    </ButtonBase>
                  ))}
                </Box>
                <TextField
                  value={place}
                  onChange={(event) => setPlace(event.target.value)}
                  label={t('newNight.fields.placeName')}
                  placeholder={t('newNight.placeholders.place')}
                  fullWidth
                  InputProps={{
                    startAdornment: <LocationOnIcon color="secondary" sx={{ mr: 1 }} />,
                  }}
                  inputProps={{ style: { fontFamily: 'Cinzel, Georgia, serif', fontSize: 20 } }}
                />
              </Stack>
            )}

            {step === 3 && (
              <Stack spacing={1.5}>
                <Typography color="text.secondary">
                  {t('newNight.companions.summary', {
                    count: invited.length,
                  })}
                </Typography>
                {loadingOptions ? (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={22} />
                    <Typography color="text.secondary">
                      {t('companions.feedback.loading')}
                    </Typography>
                  </Stack>
                ) : null}
                {!loadingOptions ? (
                  <Card
                    variant="outlined"
                    sx={{
                      borderStyle: 'dashed',
                      borderColor: 'secondary.light',
                      bgcolor: 'rgba(217, 154, 43, 0.08)',
                    }}
                  >
                    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography variant="h4">
                            {t('newNight.companions.externalTitle')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t(
                              isEditing
                                ? 'newNight.companions.externalEditSubtitle'
                                : 'newNight.companions.externalNewSubtitle'
                            )}
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<ShareIcon />}
                          onClick={handleExternalInvite}
                          disabled={externalInviteSharing}
                          sx={{ flexShrink: 0 }}
                        >
                          {externalInviteSharing
                            ? t('nights.actions.preparingShare')
                            : t('nights.actions.shareNight')}
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ) : null}
                {!loadingOptions && companions.length === 0 ? (
                  <CompanionsEmptyState
                    title={t('newNight.companions.emptyTitle')}
                    subtitle={t('newNight.companions.empty')}
                    inviteLabel={t('companions.empty.invite')}
                    onInvite={() => setInviteOpen(true)}
                  />
                ) : null}
                {!loadingOptions && companions.length > 0 && invited.length === 0 ? (
                  <Alert severity="info">
                    {t('newNight.companions.optional')}
                  </Alert>
                ) : null}
                {!loadingOptions && companions.length > 0 ? (
                  <TextField
                    type="search"
                    value={companionSearchQuery}
                    onChange={(event) => setCompanionSearchQuery(event.target.value)}
                    placeholder={t('newNight.companions.searchPlaceholder')}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : null}
                {!loadingOptions && companions.length > 0 && visibleCompanions.length === 0 ? (
                  <Card variant="outlined">
                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="h4">
                        {t('newNight.companions.noSearchResults')}
                      </Typography>
                    </CardContent>
                  </Card>
                ) : null}
                {visibleCompanions.map((companion) => {
                  const selected = invited.includes(companion.id);

                  return (
                    <Button
                      key={companion.id}
                      onClick={() => toggleFriend(companion.id)}
                      variant={selected ? 'contained' : 'outlined'}
                      color={selected ? 'primary' : 'inherit'}
                      sx={{
                        justifyContent: 'flex-start',
                        borderRadius: 3,
                        p: 1.25,
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                        <Avatar
                          src={companion.avatarUrl || undefined}
                          alt={companion.displayName}
                        />
                        <Box sx={{ flex: 1, textAlign: 'left' }}>
                          <Typography variant="h4">{companion.displayName}</Typography>
                          <PresenceBadge presence={companion.presence} noWrap />
                        </Box>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            display: 'grid',
                            placeItems: 'center',
                            border: '1px solid',
                            borderColor: selected ? 'secondary.main' : 'divider',
                            bgcolor: selected ? 'secondary.main' : 'transparent',
                          }}
                        >
                          {selected && <CheckIcon fontSize="small" />}
                        </Box>
                      </Stack>
                    </Button>
                  );
                })}
              </Stack>
            )}

            {step === 4 && (
              <Box>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {t('newNight.games.summary', {
                    count: chosenGames.length,
                  })}
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                    sx={{ mb: 1.25 }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('newNight.games.votedEyebrow')}
                      </Typography>
                      <Typography variant="h3">{t('newNight.games.votedTitle')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={800}>
                      {t('newNight.games.votedCount', {
                        count: votedGames.length,
                      })}
                    </Typography>
                  </Stack>
                  {votedGames.length === 0 ? (
                    <Card
                      variant="outlined"
                      sx={{
                        borderStyle: 'dashed',
                        bgcolor: 'rgba(255, 250, 240, 0.58)',
                      }}
                    >
                      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                        <Typography color="text.secondary">
                          {t('newNight.games.noVotesYet')}
                        </Typography>
                      </CardContent>
                    </Card>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(2, minmax(0, 1fr))',
                          md: 'repeat(3, minmax(0, 1fr))',
                        },
                        gap: 1.25,
                        minWidth: 0,
                      }}
                    >
                      {votedGames.map(({ game, voteCount, voters }) => {
                        const owners = game.owners?.length ? game.owners : [ownerFromGame(game)];
                        const selected = (game.gameIds || [game.id]).some((id) =>
                          chosenGames.includes(id)
                        );

                        return (
                          <Box
                            component="button"
                            key={game.id}
                            type="button"
                            onClick={(event) => {
                              rememberVoteScrollAnchor(event.currentTarget);
                              toggleGame(game);
                            }}
                            sx={{
                              width: '100%',
                              maxWidth: '100%',
                              minWidth: 0,
                              overflow: 'hidden',
                              border: '1px solid',
                              borderColor: selected ? 'primary.main' : 'divider',
                              borderRadius: 2,
                              p: 1.25,
                              bgcolor: selected
                                ? 'rgba(15, 76, 69, 0.08)'
                                : 'rgba(255, 250, 240, 0.7)',
                              cursor: 'pointer',
                              textAlign: 'left',
                              font: 'inherit',
                              '&:hover': {
                                borderColor: 'primary.main',
                                boxShadow: 1,
                              },
                              '&:focus-visible': {
                                outline: '3px solid',
                                outlineColor: 'secondary.light',
                                outlineOffset: 2,
                              },
                            }}
                          >
                            <Stack spacing={1} sx={{ minWidth: 0 }}>
                              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                                <GameCover game={game} size="small" />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="h4"
                                    sx={{
                                      display: '-webkit-box',
                                      overflow: 'hidden',
                                      overflowWrap: 'anywhere',
                                      textOverflow: 'ellipsis',
                                      WebkitBoxOrient: 'vertical',
                                      WebkitLineClamp: 2,
                                    }}
                                  >
                                    {gameTitle(game, t, language)}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {owners
                                      .map((owner) => owner?.displayName)
                                      .filter(Boolean)
                                      .join(', ') || t('common.unknown')}
                                  </Typography>
                                  <Typography variant="body2" color="secondary.dark" fontWeight={800}>
                                    {t('newNight.games.voteCount', { count: voteCount })}
                                  </Typography>
                                </Box>
                              </Stack>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                sx={{
                                  alignSelf: 'flex-end',
                                  flexWrap: 'wrap',
                                  justifyContent: 'flex-end',
                                  maxWidth: '100%',
                                  minWidth: 0,
                                }}
                              >
                                {voters.slice(0, 3).map((voter, index) => (
                                  <Avatar
                                    key={voter.id || voter.displayName}
                                    src={voter.avatarUrl || undefined}
                                    alt={voter.displayName || ''}
                                    title={voter.displayName || ''}
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      fontSize: '0.62rem',
                                      fontWeight: 800,
                                      bgcolor: ownerColor(voter, index),
                                      color: '#fff8e8',
                                      border: '1px solid',
                                      borderColor: '#fff8e8',
                                    }}
                                  >
                                    {(voter.displayName || '?').charAt(0)}
                                  </Avatar>
                                ))}
                              </Stack>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
                <Box sx={{ mb: 2 }}>
                  <GameFiltersBar
                    searchQuery={gameSearchQuery}
                    onSearchChange={setGameSearchQuery}
                    filters={gameFilters}
                    onFiltersChange={handleGameFiltersChange}
                    sort={gameSort}
                    onSortChange={setGameSort}
                    sortOptions={nightVoteSortOptions}
                    defaultSort={NIGHT_VOTE_DEFAULT_SORT}
                    searchPlaceholder={t('newNight.games.searchPlaceholder')}
                  />
                  {Number(gameFilters.minPlayers) === recommendedMinPlayers ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {t('newNight.games.autoPlayerFilter', {
                        count: recommendedMinPlayers,
                      })}
                    </Typography>
                  ) : null}
                </Box>
                {loadingOptions ? (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={22} />
                    <Typography color="text.secondary">
                      {t('shelf.feedback.loading')}
                    </Typography>
                  </Stack>
                ) : null}
                {!loadingOptions && games.length === 0 ? (
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 2,
                      borderStyle: 'dashed',
                      bgcolor: 'rgba(255, 250, 240, 0.64)',
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.25} alignItems="flex-start">
                        <Typography variant="h3">
                          {t('newNight.review.noGames')}
                        </Typography>
                        <Typography color="text.secondary">
                          {t('newNight.games.empty')}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                ) : null}
                {!loadingOptions && games.length > 0 && visibleGames.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('newNight.games.noFilteredResults')}
                  </Alert>
                ) : null}
                <Stack spacing={3}>
                  {visibleGameGroups.map((group) => (
                    <Box key={group.owner.id || group.owner.displayName || 'unknown'}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.25 }}>
                        {t('newNight.games.shelfGroup', {
                          name: group.owner.displayName || t('common.unknown'),
                        })}
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'repeat(2, minmax(0, 1fr))',
                            sm: 'repeat(3, minmax(0, 1fr))',
                          },
                          gap: 2,
                          minWidth: 0,
                        }}
                      >
                        {group.games.map((game) => {
                          const relatedIds = game.gameIds || [game.id];
                          const selected = relatedIds.some((id) => chosenGames.includes(id));
                          const voters = optimisticVotersForGame(game);
                          const voteBorder =
                            voters.length > 1
                              ? `linear-gradient(135deg, ${voters
                                  .map((voter, index) => {
                                    const start = Math.round((index / voters.length) * 100);
                                    const end = Math.round(((index + 1) / voters.length) * 100);

                                    return `${ownerColor(voter, index)} ${start}% ${end}%`;
                                  })
                                  .join(', ')})`
                              : ownerColor(voters[0] || selfProfile, 0);

                          return (
                            <Box
                              component="button"
                              key={game.id}
                              type="button"
                              onClick={(event) => {
                                rememberVoteScrollAnchor(event.currentTarget);
                                toggleGame(game);
                              }}
                              sx={{
                                position: 'relative',
                                width: '100%',
                                minWidth: 0,
                                p: 0,
                                border: 0,
                                bgcolor: 'transparent',
                                textAlign: 'left',
                                cursor: 'pointer',
                              }}
                            >
                              <Box
                                sx={{
                                  p: '4px',
                                  minWidth: 0,
                                  borderRadius: 2,
                                  background: selected ? voteBorder : 'transparent',
                                  boxShadow: selected
                                    ? '0 18px 30px -18px rgba(15, 76, 69, 0.95)'
                                    : '0 14px 24px -18px rgba(45, 26, 11, 0.9)',
                                  transform: selected ? 'translateY(-2px)' : 'none',
                                  transition: 'box-shadow 160ms ease, transform 160ms ease',
                                }}
                              >
                                <GameCover
                                  game={game}
                                  footerLayout="stacked"
                                  sx={{
                                    width: '100%',
                                    boxShadow: 'none',
                                  }}
                                  topOverlay={
                                    voters.length > 0 ? (
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          maxWidth: '100%',
                                        }}
                                      >
                                        {voters.slice(0, 5).map((voter, index) => (
                                          <Avatar
                                            key={voter.id || voter.displayName}
                                            src={voter.avatarUrl || undefined}
                                            alt={voter.displayName || ''}
                                            title={voter.displayName || ''}
                                            sx={{
                                              width: { xs: 26, sm: 30 },
                                              height: { xs: 26, sm: 30 },
                                              ml: index === 0 ? 0 : -0.75,
                                              fontSize: '0.68rem',
                                              fontWeight: 800,
                                              bgcolor: ownerColor(voter, index),
                                              color: '#fff8e8',
                                              border: '2px solid',
                                              borderColor: '#fff8e8',
                                              boxShadow: '0 10px 20px -12px rgba(0,0,0,0.95)',
                                            }}
                                          >
                                            {(voter.displayName || '?').charAt(0)}
                                          </Avatar>
                                        ))}
                                      </Box>
                                    ) : null
                                  }
                                  footerEnd={
                                    <BggRatingMugs
                                      game={game}
                                      showValue={false}
                                      sx={{ flex: '0 0 auto' }}
                                    />
                                  }
                                />
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {step === 5 && (
              <Stack spacing={2.5}>
                <Alert severity="info">
                  {t('newNight.review.permissionsIntro')}
                </Alert>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                  }}
                >
                  <Card variant="outlined" sx={{ bgcolor: 'rgba(255,250,240,0.48)' }}>
                    <CardContent>
                      <Stack spacing={1.25}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('newNight.review.details')}
                        </Typography>
                        <Typography variant="h3">{nightTitle}</Typography>
                        <Typography color="text.secondary">
                          {reviewWeekday}, {reviewDate} - {reviewTime}
                        </Typography>
                        <Typography color="text.secondary">
                          {place.trim() || t('newNight.summary.noPlace')}
                        </Typography>
                        <Button size="small" onClick={() => setStep(0)} sx={{ alignSelf: 'flex-start' }}>
                          {t('newNight.review.editDetails')}
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ bgcolor: 'rgba(255,250,240,0.48)' }}>
                    <CardContent>
                      <Stack spacing={1.25}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('newNight.review.invitationResponses')}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={requiresResponse}
                              onChange={(event) => setRequiresResponse(event.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography fontWeight={900}>
                                {t('newNight.review.requiresResponseLabel')}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {requiresResponse
                                  ? t('newNight.review.requiresResponseOn')
                                  : t('newNight.review.requiresResponseOff')}
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start', m: 0 }}
                        />
                      </Stack>
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ bgcolor: 'rgba(255,250,240,0.48)' }}>
                    <CardContent>
                      <Stack spacing={1.25}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('newNight.review.roles')}
                        </Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            src={selfProfile?.avatarUrl || undefined}
                            alt={selfProfile?.displayName || t('common.you')}
                          />
                          <Box>
                            <Typography fontWeight={800}>
                              {selfProfile?.displayName || t('common.you')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('newNight.review.organizerRights')}
                            </Typography>
                          </Box>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {invited.length > 0
                            ? t('newNight.review.participantRights', { count: invited.length })
                            : t('newNight.review.noParticipants')}
                        </Typography>
                        <Button size="small" onClick={() => setStep(3)} sx={{ alignSelf: 'flex-start' }}>
                          {t('newNight.review.editCompanions')}
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ bgcolor: 'rgba(255,250,240,0.48)' }}>
                    <CardContent>
                      <Stack spacing={1.25}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('newNight.review.games')}
                        </Typography>
                        {chosenGameOptions.length === 0 ? (
                          <Typography color="text.secondary">
                            {t('newNight.review.noGames')}
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            {chosenGameOptions.map((game) => (
                              <Stack
                                key={game.id}
                                direction="row"
                                spacing={1.25}
                                alignItems="center"
                                sx={{ minWidth: 0 }}
                              >
                                <GameCover game={game} size="small" />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    fontWeight={800}
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {gameTitle(game, t, language)}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {(game.owners || [])
                                      .map((owner) => owner.displayName)
                                      .filter(Boolean)
                                      .join(', ') || t('common.unknown')}
                                  </Typography>
                                </Box>
                              </Stack>
                            ))}
                          </Stack>
                        )}
                        <Button size="small" onClick={() => setStep(4)} sx={{ alignSelf: 'flex-start' }}>
                          {t('newNight.review.editGames')}
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ bgcolor: 'rgba(15, 76, 69, 0.06)' }}>
                    <CardContent>
                      <Stack spacing={1.25}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('newNight.review.votes')}
                        </Typography>
                        <Typography color="text.secondary">
                          {chosenGameOptions.length > 0
                            ? t('newNight.review.votesClose', {
                                date: reviewVotingCloseDate,
                                time: reviewVotingCloseTime,
                              })
                            : t('newNight.review.votesNeedGames')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('newNight.review.afterSeal')}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              </Stack>
            )}

            <Stack
              direction="row"
              spacing={1.5}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                position: { xs: 'fixed', md: 'static' },
                left: {
                  xs: 'max(1rem, calc(env(safe-area-inset-left, 0px) + 1rem))',
                  md: 'auto',
                },
                right: {
                  xs: 'max(1rem, calc(env(safe-area-inset-right, 0px) + 1rem))',
                  md: 'auto',
                },
                bottom: {
                  xs: 'calc(5.5rem + env(safe-area-inset-bottom, 0px))',
                  md: 'auto',
                },
                zIndex: (theme) => theme.zIndex.appBar - 1,
                mt: 'auto',
                mx: { xs: 0, md: 0 },
                p: { xs: 1.25, md: 0 },
                pt: { xs: 1.25, md: 4 },
                border: { xs: '1px solid', md: 0 },
                borderColor: { xs: 'rgba(15, 76, 69, 0.16)', md: 'transparent' },
                borderRadius: { xs: 3, md: 0 },
                bgcolor: { xs: 'rgba(255, 250, 239, 0.94)', md: 'transparent' },
                boxShadow: {
                  xs: '0 18px 42px -24px rgba(6, 37, 34, 0.58)',
                  md: 'none',
                },
                backdropFilter: { xs: 'blur(16px)', md: 'none' },
              }}
            >
              <Button
                onClick={() => (step === 0 || voteOnly ? handleBackToNights() : setStep(step - 1))}
              >
                {step === 0 || voteOnly ? t('common.cancel') : t('common.back')}
              </Button>
              <Button
                variant="contained"
                color={actionIsLast ? 'secondary' : 'primary'}
                endIcon={actionIsLast ? <CheckIcon /> : undefined}
                disabled={saving || !canContinue}
                onClick={() => (actionIsLast ? handleSealNight() : setStep(step + 1))}
              >
                {actionIsLast
                  ? saving
                    ? t('newNight.actions.sealing')
                    : voteOnly
                      ? t('newNight.actions.saveVotes')
                      : t('newNight.actions.sealNight')
                  : t('common.continue')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
      <InviteCompanionSheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />
    </Box>
  );
};

export default NewNightPage;
